import { z } from 'zod';
import { idSchema, isoDateTimeSchema, moneySchema } from './common.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { compareDecimal } from '../utils/decimal.js';
import { paginationMetaSchema } from './pagination.js';
const assetCreditAmountSchema = moneySchema.refine((value) => compareDecimal(value, '0') > 0);
export const assetSourceAllocationSchema = z
    .object({
    assetId: idSchema,
    sourcePosition: z.number().int().positive(),
    amount: assetCreditAmountSchema,
})
    .strict();
export const assetCreditAllocationSchema = z
    .object({
    assetId: idSchema.nullable(),
    sourcePosition: z.number().int().positive(),
    creditPosition: z.number().int().positive(),
    amount: assetCreditAmountSchema,
})
    .strict();
export const assetCreditReviewSchema = z
    .object({
    sourceAllocations: z.array(assetSourceAllocationSchema).min(1).max(500),
    allocations: z.array(assetCreditAllocationSchema).min(1).max(500),
})
    .strict()
    .superRefine((value, context) => {
    for (const [field, rows] of [
        ['sourceAllocations', value.sourceAllocations],
        ['allocations', value.allocations],
    ]) {
        const seen = new Set();
        for (const [index, row] of rows.entries()) {
            const key = JSON.stringify([
                row.assetId,
                row.sourcePosition,
                'creditPosition' in row ? row.creditPosition : null,
            ]);
            if (seen.has(key))
                context.addIssue({
                    code: 'custom',
                    path: [field, index],
                    message: 'ledgerClosing.errors.assetCreditProof',
                });
            seen.add(key);
        }
    }
});
export const foreignVendorCreditFiscalReviewSchema = z
    .object({
    sourceFiscalReviewId: idSchema.nullable(),
    sourcePositions: z
        .array(z
        .object({
        creditPosition: z.number().int().positive(),
        sourcePosition: z.number().int().positive(),
    })
        .strict())
        .min(1)
        .max(200),
    evidenceText: z.string().trim().min(20).max(10000),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.sourcePositions.map((row) => row.creditPosition)).size !==
        value.sourcePositions.length)
        context.addIssue({
            code: 'custom',
            path: ['sourcePositions'],
            message: 'supplierCredits.errors.foreignFiscal',
        });
});
export const vendorCreditReviewSchema = z
    .object({
    sourceInvoiceId: idSchema,
    reason: z.string().trim().min(10).max(2000),
    zeroVatSourceCode: z
        .enum(['EXEMPT', 'SMALL_BUSINESS', 'OUT_OF_SCOPE', 'ZERO_RATE', 'SELF_ASSESSED'])
        .nullable(),
    taxClearingAccountId: idSchema,
    reviewedAt: isoDateTimeSchema,
    assetReview: assetCreditReviewSchema.nullish(),
    foreignFiscalReview: foreignVendorCreditFiscalReviewSchema.nullish(),
})
    .strict();
export const vendorCreditReviewSaveSchema = vendorCreditReviewSchema
    .omit({ reviewedAt: true })
    .extend({
    version: z.number().int().positive(),
});
export const vendorCreditOffsetSchema = z.object({
    targetInvoiceId: idSchema,
    amount: moneySchema.refine((value) => /^\d/u.test(value) && !/^0(?:\.0+)?$/u.test(value)),
    effectiveDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: z.string().trim().min(8).max(100),
});
export const vendorCreditOffsetResultSchema = z.object({
    creditInvoiceId: idSchema,
    targetInvoiceId: idSchema,
    creditSettlementId: idSchema,
    targetSettlementId: idSchema,
    journalId: idSchema.nullable(),
    amount: moneySchema,
});
export const vendorCreditOffsetHistorySchema = z.object({
    data: z.array(vendorCreditOffsetResultSchema.extend({
        effectiveDate: ledgerDateSchema,
        reason: z.string(),
        reversed: z.boolean(),
    })),
    meta: paginationMetaSchema,
});
export const vendorCreditOffsetReverseSchema = z.object({
    effectiveDate: ledgerDateSchema,
    reason: z.string().trim().min(10).max(2000),
    idempotencyKey: z.string().trim().min(8).max(100),
});
//# sourceMappingURL=vendor-credits.js.map